import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { HeyGenConf } from '@shared/index';
import { waitSeconds } from '@reflact/tsutil';
import { firstValueFrom } from 'rxjs';
import { LoginService } from './login.service';

export type Avatar = Omit<HeyGenConf, "apiKey">;

export type LiveAvatarEntry = {
  id: string;
  name: string;
  previewUrl: string;
  avatarSource?: 'expressive' | 'customAgent';
  // Nur bei D-ID-Custom-Agents relevant: true = global verfuegbarer Standard-Avatar, false = Mandanten-eigener Agent.
  isGlobalDefault?: boolean;
  // Nur bei D-ID-Custom-Agents relevant: hat dieser Avatar einen echten Greenscreen-Hintergrund?
  isGreenscreen?: boolean;
  // Nur bei D-ID-Custom-Agents relevant: D-ID Idle-Video (hoeher aufgeloest als previewUrl), falls vorhanden.
  idleVideoUrl?: string;
};

@Injectable({
  providedIn: 'root'
})
export class AvatarService {
  // Signal-backed, weil nach await gesetzt und in OnPush-Templates gelesen.
  private readonly _avatars = signal<Avatar[]>([]);
  public get avatars(): Avatar[] { return this._avatars(); }
  public set avatars(value: Avatar[]) { this._avatars.set(value); }
  private isLoadedingAvatars = false;
  private avatarPreviewCache = new Map<string, string>();
  constructor(private http: HttpClient, public loginService: LoginService) { }

  public async getAvatars(): Promise<Avatar[]> {
    if (this.isLoadedingAvatars) {
      await waitSeconds(1);
      return await this.getAvatars();
    }
    if (this.avatars.length == 0) {
      this.isLoadedingAvatars = true;
      try {
        this.avatars = await firstValueFrom(this.http.get<Avatar[]>(`/api/toolbox/bot/admin/listavatars`));
      } catch (e) {
        console.error(e);
      }
      this.isLoadedingAvatars = false;
    }
    return this.avatars;
  }

  public async getAvatarById(avatarId: string) {
    const avatars = await this.getAvatars();
    return avatars.find(a => a.id == avatarId);
  }

  public async getAvatarPreviewImage(avatarId: string): Promise<string> {
    const u = this.avatarPreviewCache.get(avatarId);
    if (u) {
      return u;
    }
    const { url } = await firstValueFrom(this.http.get<{ url: string }>(`/api/toolbox/heygen/avatarimage/${avatarId}`));
    this.avatarPreviewCache.set(avatarId, url);
    return url;
  }

  public async getAvatar(avatarId: string) {
    return await firstValueFrom(this.http.get<Avatar>(`/api/toolbox/heygen/avatarinfo/${avatarId}`));
  }

  // ─── LiveAvatar ────────────────────────────────────────────────────────────
  private liveAvatarsCache = new Map<string, LiveAvatarEntry[]>();
  private isLoadingLiveAvatars = false;

  public async getLiveAvatars(orgShort?: string): Promise<LiveAvatarEntry[]> {
    if (this.isLoadingLiveAvatars) {
      await waitSeconds(1);
      return await this.getLiveAvatars(orgShort);
    }
    const cacheKey = orgShort ?? "";
    if (!this.liveAvatarsCache.has(cacheKey)) {
      this.isLoadingLiveAvatars = true;
      try {
        const url = orgShort
          ? `/api/toolbox/bot/admin/listliveavatars?org=${encodeURIComponent(orgShort)}`
          : `/api/toolbox/bot/admin/listliveavatars`;
        const avatars = await firstValueFrom(this.http.get<LiveAvatarEntry[]>(url));
        this.liveAvatarsCache.set(cacheKey, avatars);
      } catch (e) {
        console.error(e);
        this.liveAvatarsCache.set(cacheKey, []);
      }
      this.isLoadingLiveAvatars = false;
    }
    return this.liveAvatarsCache.get(cacheKey) ?? [];
  }

  // ─── D-ID Avatar ───────────────────────────────────────────────────────────
  private didAvatars: LiveAvatarEntry[] = [];
  private isLoadingDIdAvatars = false;

  public async getDIdAvatars(): Promise<LiveAvatarEntry[]> {
    if (this.isLoadingDIdAvatars) {
      await waitSeconds(1);
      return await this.getDIdAvatars();
    }
    if (this.didAvatars.length === 0) {
      this.isLoadingDIdAvatars = true;
      try {
        this.didAvatars = await firstValueFrom(this.http.get<LiveAvatarEntry[]>(`/api/toolbox/bot/admin/listdidavatars`));
      } catch (e) {
        console.error(e);
      }
      this.isLoadingDIdAvatars = false;
    }
    return this.didAvatars;
  }
}